/**
 * Minimal tween system standing in for Clutter's easing and transitions. Each tween is keyed so a
 * newer animation on the same property replaces the older one, like Clutter transitions did.
 */

export type Ease = (t: number) => number;

export const ease = {
  linear: (t: number): number => t,
  outCubic: (t: number): number => 1 - (1 - t) ** 3,
  inOutCubic: (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  inQuad: (t: number): number => t * t,
  inOutQuad: (t: number): number => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2),
  outBack: (t: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
  },
  outBounce: (t: number): number => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) {
      const u = t - 1.5 / d1;
      return n1 * u * u + 0.75;
    }
    if (t < 2.5 / d1) {
      const u = t - 2.25 / d1;
      return n1 * u * u + 0.9375;
    }
    const u = t - 2.625 / d1;
    return n1 * u * u + 0.984375;
  },
};

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

export interface TweenOptions {
  duration: number;
  delay?: number;
  ease?: Ease;
  /** Called with the eased progress, from 0 to 1. */
  step: (t: number) => void;
  /** Called once the tween finishes normally. */
  onDone?: () => void;
  /** Called when the tween is replaced or cancelled before it finishes. */
  onCancel?: () => void;
}

interface ActiveTween extends TweenOptions {
  readonly start: number;
  readonly easing: Ease;
}

export class Tweener {
  private readonly tweens = new Map<string, ActiveTween>();

  get size(): number {
    return this.tweens.size;
  }

  add(key: string, options: TweenOptions, now: number): void {
    this.cancel(key);
    this.tweens.set(key, {
      ...options,
      start: now + (options.delay ?? 0),
      easing: options.ease ?? ease.linear,
    });
  }

  cancel(key: string): void {
    const tween = this.tweens.get(key);
    if (!tween) return;
    this.tweens.delete(key);
    tween.onCancel?.();
  }

  /** Cancels every tween whose key starts with `prefix`. */
  cancelPrefix(prefix: string): void {
    for (const key of Array.from(this.tweens.keys())) {
      if (key.startsWith(prefix)) this.cancel(key);
    }
  }

  update(now: number): void {
    for (const [key, tween] of Array.from(this.tweens)) {
      // The tween may have been replaced or cancelled by an earlier callback in this loop.
      if (this.tweens.get(key) !== tween || now < tween.start) continue;

      const raw = tween.duration <= 0 ? 1 : Math.min(1, (now - tween.start) / tween.duration);
      tween.step(tween.easing(raw));

      if (raw >= 1) {
        this.tweens.delete(key);
        tween.onDone?.();
      }
    }
  }
}
